import React, { useRef, useId } from 'react';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';

import { IconName, IconSize } from '@components/icon';

import {
  useModalKeyboard,
  useModalFocus,
  useModalFocusTrap,
  useBodyScrollLock,
  useResponsiveButtonSize,
} from './hooks';

import { ModalContent } from './ModalContent';

import './Modal.scss';

/*
Example how to use the Modal component:

<Modal
    isOpen={isModalOpen}
    onClose={() => setModalOpen(false)}
    title="Are you sure you want to sign out?"
    content="You can sign in with a different account."
    iconName="alert"
    iconSize="large"
    confirmLabel="Sign Out"
    cancelLabel="Cancel"
    onConfirm={handleConfirm}
    isDismissible={true} // Clicking outside the modal or pressing Escape closes it (default: true)
/>
*/

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Modal title text */
  title?: string;
  /** Modal body content, rendered inside Body — plain text or inline nodes */
  content?: React.ReactNode;
  /** Block-level custom content rendered between the body and the footer */
  children?: React.ReactNode;
  /** Icon name to display */
  iconName?: IconName;
  /** Size of the icon */
  iconSize?: IconSize;
  /** Label for confirm button. Default: 'Confirm' */
  confirmLabel?: string;
  /** Label for cancel button. */
  cancelLabel?: string;
  /** Additional CSS class */
  className?: string;
  /** Whether to show the footer buttons. Default: true */
  hasFooter?: boolean;
  /** Whether the modal can be dismissed by clicking outside or pressing Escape. Default: true */
  isDismissible?: boolean;
  /** Callback fired when confirm button is clicked */
  onConfirm?: () => void;
  /** Callback fired when modal should close */
  onClose: () => void;
  /** Whether to prevent auto tracking. Default: false */
  preventAutoTracking?: boolean;
}

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 0.8 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { x: '-50%', y: 'calc(-50% - 10%)', opacity: 0 },
  visible: { x: '-50%', y: '-50%', opacity: 1 },
  exit: { x: '-50%', y: 'calc(-50% - 10%)', opacity: 0 },
};

/**
 * Modal component for displaying dialog overlays with customizable content and actions.
 *
 * The Modal component provides a focused dialog interface that overlays the current page,
 * allowing users to interact with important information or confirm actions. It includes
 * built-in accessibility features such as focus management, keyboard navigation (Escape to close),
 * and ARIA attributes. The modal automatically locks body scroll when open and restores focus
 * to the triggering element when closed.
 *
 * Features:
 * - Customizable icon, title, and content
 * - Optional confirm and cancel action buttons
 * - Dismissible via overlay click or Escape key (configurable)
 * - Automatic body scroll lock
 * - Focus management and keyboard navigation
 * - Responsive button sizing
 * - Smooth animations via Framer Motion
 *
 * @param props - Modal component props
 * @param props.isOpen - Whether the modal is open
 * @param props.onClose - Callback fired when modal should close
 * @param props.title - Modal title text
 * @param props.content - Modal body content, rendered inside Body — plain text or inline nodes
 * @param props.children - Block-level custom content rendered between the body and the footer
 * @param props.iconName - Icon name to display
 * @param props.iconSize - Size of the icon
 * @param props.confirmLabel - Label for confirm button. Default: 'Confirm'
 * @param props.cancelLabel - Label for cancel button.
 * @param props.className - Additional CSS class
 * @param props.hasFooter - Whether to show the footer buttons. Default: true
 * @param props.isDismissible - Whether the modal can be dismissed by clicking outside or pressing Escape. Default: true
 * @param props.onConfirm - Callback fired when confirm button is clicked
 * @param props.preventAutoTracking - Whether to prevent auto tracking. Default: false
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  content,
  children,
  iconName,
  iconSize,
  confirmLabel = 'Confirm',
  cancelLabel,
  className,
  hasFooter = true,
  isDismissible = true,
  onConfirm,
  preventAutoTracking = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const contentId = useId();

  useModalKeyboard(isOpen, isDismissible, onClose);
  useModalFocus(isOpen, modalRef, title ? titleId : undefined);
  useModalFocusTrap(isOpen, modalRef);
  useBodyScrollLock(isOpen);

  const buttonSize = useResponsiveButtonSize();

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDismissible) return;

    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="vds-modal__overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            onMouseDown={handleOverlayClick}
          />
          <motion.div
            className={classNames('vds-modal', className, {
              'vds-modal--no-footer': !hasFooter,
            })}
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={content || content === 0 ? contentId : undefined}
            tabIndex={-1}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            // Prevent clicks inside the modal from bubbling up to the overlay,
            // which would otherwise trigger the modal to close.
            onMouseDown={e => e.stopPropagation()}>
            <ModalContent
              iconName={iconName}
              iconSize={iconSize}
              title={title}
              content={content}
              titleId={titleId}
              contentId={contentId}
              hasFooter={hasFooter}
              confirmLabel={confirmLabel}
              cancelLabel={cancelLabel}
              buttonSize={buttonSize}
              onConfirm={onConfirm}
              onClose={onClose}
              preventAutoTracking={preventAutoTracking}>
              {children}
            </ModalContent>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
